import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'

const NewStockEntry = () => {
  const smallShopMode = isSmallShopMode()
  const [formData, setFormData] = useState({
    warehouse_id: '',
    branch_id: '',
    group_notes: ''
  })
  const [items, setItems] = useState([
    { product_id: '', quantity: '', purchase_price: '', selling_price: '', reorder_level: '', supplier_id: '', notes: '' }
  ])
  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [products, setProducts] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingData(true)
        const locationRequest = smallShopMode
          ? apiClient.get(API_ENDPOINTS.BRANCHES)
          : apiClient.get(API_ENDPOINTS.WAREHOUSES)

        const [locationResponse, productsResponse, suppliersResponse] = await Promise.all([
          locationRequest,
          apiClient.get(API_ENDPOINTS.PRODUCTS),
          apiClient.get(API_ENDPOINTS.SUPPLIERS)
        ])

        const locationList = locationResponse.data?.results || locationResponse.data || []
        if (smallShopMode) {
          setBranches(locationList)
        } else {
          setWarehouses(locationList)
        }
        setProducts(productsResponse.data?.results || productsResponse.data || [])
        setSuppliers(suppliersResponse.data?.results || suppliersResponse.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: smallShopMode
            ? 'Failed to load branches, products, or suppliers. Please refresh the page.'
            : 'Failed to load warehouses, products, or suppliers. Please refresh the page.'
        })
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [smallShopMode])

  const locationOptions = useMemo(() => {
    const list = smallShopMode ? branches : warehouses
    return list.map((loc) => ({
      value: loc.id,
      label: loc.name || (smallShopMode ? `Branch ${loc.id}` : `Warehouse ${loc.id}`)
    }))
  }, [smallShopMode, branches, warehouses])

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.id,
      label: `${product.name} (${product.sku})`
    }))
  }, [products])

  const supplierOptions = useMemo(() => {
    return suppliers.map(supplier => ({
      value: supplier.id,
      label: supplier.name
    }))
  }, [suppliers])

  const selectedLocation = useMemo(() => {
    const id = smallShopMode ? formData.branch_id : formData.warehouse_id
    return id ? locationOptions.find(opt => opt.value === parseInt(id, 10)) : null
  }, [smallShopMode, formData.branch_id, formData.warehouse_id, locationOptions])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value === '' ? '' : value
    })
  }

  const handleSelectChange = (selectedOption, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    })
  }

  const handleItemChange = (index, field, value) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [field]: value === '' ? '' : value
    }
    setItems(newItems)
  }

  const handleItemSelectChange = (index, selectedOption, fieldName) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    }
    setItems(newItems)
  }

  const addItem = () => {
    setItems([
      ...items,
      { product_id: '', quantity: '', purchase_price: '', selling_price: '', reorder_level: '', supplier_id: '', notes: '' }
    ])
  }

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const locationId = smallShopMode ? formData.branch_id : formData.warehouse_id
      if (!locationId) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: smallShopMode ? 'Please select a branch.' : 'Please select a warehouse.'
        })
        setLoading(false)
        return
      }

      const validItems = items.filter(item =>
        item.product_id && item.quantity && item.purchase_price && item.reorder_level
      )

      if (validItems.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please add at least one product with quantity, purchase price, and reorder level.'
        })
        setLoading(false)
        return
      }

      if (smallShopMode) {
        const payload = {
          branch_stock_entries: validItems.map(item => {
            const entry = {
              branch_id: parseInt(locationId, 10),
              product_id: parseInt(item.product_id, 10),
              quantity: parseFloat(item.quantity),
              purchase_price: parseFloat(item.purchase_price),
              reorder_level: parseInt(item.reorder_level, 10)
            }
            if (item.selling_price !== '') {
              entry.selling_price = parseFloat(item.selling_price)
            }
            if (item.supplier_id) {
              entry.supplier_id = parseInt(item.supplier_id, 10)
            }
            if (item.notes) {
              entry.notes = item.notes
            }
            return entry
          })
        }
        await apiClient.post(API_ENDPOINTS.BRANCH_STOCK_BULK, payload)
      } else {
        const payload = {
          stock_entries: validItems.map(item => {
            const entry = {
              warehouse_id: parseInt(locationId, 10),
              product_id: parseInt(item.product_id, 10),
              quantity: parseFloat(item.quantity),
              purchase_price: parseFloat(item.purchase_price),
              reorder_level: parseFloat(item.reorder_level)
            }
            if (item.selling_price !== '') {
              entry.selling_price = parseFloat(item.selling_price)
            }
            if (item.supplier_id) {
              entry.supplier_id = parseInt(item.supplier_id, 10)
            }
            if (item.notes) {
              entry.notes = item.notes
            }
            return entry
          })
        }
        await apiClient.post('/api/stock/entries/bulk/', payload)
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Stock entry created successfully!',
        confirmButtonText: 'OK'
      })

      setFormData({
        warehouse_id: '',
        branch_id: '',
        group_notes: ''
      })
      setItems([{ product_id: '', quantity: '', purchase_price: '', selling_price: '', reorder_level: '', supplier_id: '', notes: '' }])
    } catch (error) {
      console.error('Error creating stock entry:', error)

      let errorMessage = 'Failed to create stock entry. Please try again.'

      if (error.response?.data) {
        const errorData = error.response.data

        if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else {
          const firstError = Object.values(errorData)[0]
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  const locationFieldName = smallShopMode ? 'branch_id' : 'warehouse_id'
  const locationLabel = smallShopMode ? 'Branch' : 'Warehouse'

  return (
    <>
      <div className="pagetitle">
        <h1>New Stock Entry</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">New Stock Entry</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-box-arrow-in-down me-2"></i>
                  New Stock Entry
                </h5>

                {smallShopMode && (
                  <div className="alert alert-info py-2" role="alert">
                    <i className="bi bi-shop me-2"></i>
                    Small shop mode: stock is added directly to a branch.
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="location_id" className="col-sm-2 col-form-label">
                      {locationLabel} <span className="text-danger">*</span>
                    </label>
                    <div className="col-sm-10">
                      <Select
                        id="location_id"
                        name={locationFieldName}
                        value={selectedLocation}
                        onChange={(option) => handleSelectChange(option, locationFieldName)}
                        options={locationOptions}
                        isClearable
                        isSearchable
                        placeholder={`Select ${locationLabel}`}
                        isDisabled={loading || fetchingData}
                        required
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Products</label>
                    <div className="col-sm-10">
                      {items.map((item, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="card-title mb-0">Product {index + 1}</h6>
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeItem(index)}
                                  disabled={loading || fetchingData}
                                >
                                  <i className="bi bi-trash"></i> Remove
                                </button>
                              )}
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Product <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  value={item.product_id ? productOptions.find(opt => opt.value === parseInt(item.product_id, 10)) : null}
                                  onChange={(option) => handleItemSelectChange(index, option, 'product_id')}
                                  options={productOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Product"
                                  isDisabled={loading || fetchingData}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      borderColor: '#ced4da',
                                      '&:hover': {
                                        borderColor: '#ced4da'
                                      }
                                    })
                                  }}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                  placeholder="Enter quantity"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Purchase Price <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.purchase_price}
                                  onChange={(e) => handleItemChange(index, 'purchase_price', e.target.value)}
                                  placeholder="Enter purchase price per unit"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Selling Price
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.selling_price}
                                  onChange={(e) => handleItemChange(index, 'selling_price', e.target.value)}
                                  placeholder="Enter selling price per unit"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Reorder Level <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  className="form-control"
                                  value={item.reorder_level}
                                  onChange={(e) => handleItemChange(index, 'reorder_level', e.target.value)}
                                  placeholder="Enter minimum stock level"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">Supplier</label>
                              <div className="col-sm-9">
                                <Select
                                  value={item.supplier_id ? supplierOptions.find(opt => opt.value === parseInt(item.supplier_id, 10)) : null}
                                  onChange={(option) => handleItemSelectChange(index, option, 'supplier_id')}
                                  options={supplierOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Supplier (Optional)"
                                  isDisabled={loading || fetchingData}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      borderColor: '#ced4da',
                                      '&:hover': {
                                        borderColor: '#ced4da'
                                      }
                                    })
                                  }}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <label className="col-sm-3 col-form-label">Notes</label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={item.notes}
                                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                  placeholder="Enter notes (Optional)"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={addItem}
                        disabled={loading || fetchingData}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Another Product
                      </button>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="group_notes" className="col-sm-2 col-form-label">Group Notes</label>
                    <div className="col-sm-10">
                      <textarea
                        className="form-control"
                        id="group_notes"
                        name="group_notes"
                        rows="3"
                        value={formData.group_notes}
                        onChange={handleChange}
                        placeholder="Enter group notes (Optional)"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || fetchingData}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Create Stock Entry
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default NewStockEntry
